/**
 * Hero card configurations for the merged scroll-video narrative.
 *
 * The hero is now ONE big sticky container with both videos crossfading
 * inside it. Card scroll ranges are in 0..1 across the WHOLE narrative
 * (not per-video). The crossfade is a CINEMATIC scale + blur + opacity
 * transition (not a snap), and runs across 12% of total scroll progress
 * (0.44 → 0.56) so it has time to be felt:
 *
 *   Cards 1–3 (over V1, light bg)     ranges within [0, 0.43]
 *   ── cinematic crossfade 0.44 → 0.56 ──
 *   Cards 4–5 (over V2, light → dark) ranges within [0.57, 1.0]
 *
 * Cards 3 and 4 fully exit/enter OUTSIDE the crossfade window so the
 * blurred/scaled transition isn't cluttered by floating text.
 *
 * The bg transition (light → dark) happens at 0.85–0.95 so the closing
 * card (LEVERT) lands on a dark background — that's also the visual
 * handoff into the dark marketing sections below the hero.
 *
 * Story arc:
 * 1. STARTSTEDET     — your home and your stuff
 * 2. HVA SOM HELST   — anything you can ship
 * 3. FORBINDELSEN    — a carrier is already on your route
 * ── (cinematic crossfade: camera punches through V1 → V2) ──
 * 4. MATCH           — quotes within hours, not days
 * 5. LEVERT          — safely delivered, both rated
 */

import type { CardConfig } from "./components/ScrollVideoNarrative";

export const HERO_CARDS: CardConfig[] = [
  // V1 territory (light bg)
  { key: "hero.card1", side: "left", range: [0.0, 0.03, 0.12, 0.16], dark: false },
  { key: "hero.card2", side: "right", range: [0.17, 0.2, 0.28, 0.32], dark: false },
  { key: "hero.card3", side: "left", range: [0.32, 0.35, 0.4, 0.43], dark: false },
  // ── cinematic crossfade 0.44 → 0.56 (12% of scroll) ──
  // V2 territory (light bg → dark bg at end)
  { key: "hero.card4", side: "right", range: [0.57, 0.61, 0.74, 0.79], dark: false },
  { key: "hero.card5", side: "center", range: [0.81, 0.85, 0.96, 1.0], dark: true },
];

/** Cinematic crossfade range (in scroll progress 0..1).
 *  Wider than a simple crossfade — gives the scale/blur/opacity transforms
 *  time to be felt as a deliberate transition, not a snap. */
export const HERO_CROSSFADE_RANGE: [number, number] = [0.44, 0.56];

/** Background transition (light → dark) range. The 5th card lands on dark. */
export const HERO_BG_TRANSITION = {
  range: [0.85, 0.9, 0.95] as [number, number, number],
};
